import { useContext, useState, useCallback } from "react";
import { BookingContext } from "../../../../../components/BookingContext";
import Swal from "sweetalert2";
import { getAvailableRooms } from "../getAvailableRooms";

export const useBookingSideBar = ({
  adultsFilter,
  childrenFilter,
  rooms,
  fromdate,
  todate,
  setShowSidebar,
}) => {
  const { bookingSidebar, setBookingSidebar } = useContext(BookingContext);
  const [expandedRooms, setExpandedRooms] = useState({});

  const handlePickAddOnService = useCallback(
    (service) => {
      setBookingSidebar((prev) => [
        ...prev,
        {
          type: "addonservice",
          serviceName: service.serviceName,
          servicePrice: service.servicePrice,
          chargeType: service.ChargeType,
          serviceDescription: service.serviceDescription,
          serviceId: service._id,
        },
      ]);
    },
    [setBookingSidebar]
  );

  const handleRemoveAddOnService = useCallback(
    (serviceId) => {
      setBookingSidebar((prev) =>
        prev.filter((plan) => plan.serviceId !== serviceId)
      );
    },
    [setBookingSidebar]
  );

  const handlePickRatePlan = useCallback(
    (ratePlanName, rate) => {
      const room = rooms.find((r) => r._id === rate.roomId);
      const availableRooms = getAvailableRooms({
        room,
        excludeRatePlanId: rate.ratePlanId,
        fromdate,
        todate,
        bookingSidebar,
      });

      setBookingSidebar((prev) => [
        ...prev,
        {
          ...rate,
          selectedRooms: availableRooms > 0 ? 1 : 0,
          selectedAdults: { 0: adultsFilter },
          selectedChildren: { 0: childrenFilter },
          extraBed: { 0: false },
          roomId: rate.roomId,
        },
      ]);
      setExpandedRooms((prevState) => ({
        ...prevState,
        [rate.roomId]: false,
      }));

      setShowSidebar(true);
    },
    [
      rooms,
      fromdate,
      todate,
      bookingSidebar,
      setBookingSidebar,
      setShowSidebar,
      adultsFilter,
      childrenFilter,
    ]
  );

  const handleRoomChange = useCallback(
    (ratePlanId, roomId, numberOfRooms) => {
      setBookingSidebar((prev) => {
        const selectedRatePlan = prev.find(
          (plan) => plan.ratePlanId === ratePlanId && plan.roomId === roomId
        );
        if (!selectedRatePlan) return prev;

        const room = rooms.find((r) => r._id === roomId);
        const maxSelectableRooms = getAvailableRooms({
          room,
          excludeRatePlanId: ratePlanId,
          fromdate,
          todate,
          bookingSidebar,
        });

        if (numberOfRooms <= maxSelectableRooms) {
          return prev.map((plan) =>
            plan.ratePlanId === ratePlanId && plan.roomId === roomId
              ? { ...plan, selectedRooms: numberOfRooms }
              : plan
          );
        } else {
          Swal.fire({
            title: "Error",
            text: `Only ${maxSelectableRooms} rooms are available for the selected date range.`,
            icon: "error",
          });
          return prev;
        }
      });
    },
    [rooms, fromdate, todate, bookingSidebar, setBookingSidebar]
  );

  const handleGuestInput = useCallback(
    (ratePlanId, roomId, roomIndex, type, value) => {
      setBookingSidebar((prevSidebar) =>
        prevSidebar.map((plan) => {
          if (plan.ratePlanId === ratePlanId && plan.roomId === roomId) {
            const updatedField = {
              ...plan[
                `selected${type.charAt(0).toUpperCase() + type.slice(1)}`
              ],
              [roomIndex]: value,
            };

            return {
              ...plan,
              [`selected${type.charAt(0).toUpperCase() + type.slice(1)}`]:
                updatedField,
            };
          }
          return plan;
        })
      );
    },
    [setBookingSidebar]
  );

  const toggleExtraBed = useCallback(
    (ratePlanId, roomId, roomIndex) => {
      setBookingSidebar((prev) => {
        return prev.map((plan) =>
          plan.ratePlanId === ratePlanId && plan.roomId === roomId
            ? {
                ...plan,
                extraBed: {
                  ...plan.extraBed,
                  [roomIndex]: !plan.extraBed?.[roomIndex],
                },
              }
            : plan
        );
      });
    },
    [setBookingSidebar]
  );

  const handleRemoveIndividualRoom = useCallback(
    (ratePlanId, roomId, roomIndex) => {
      setBookingSidebar((prev) => {
        return prev
          .map((plan) => {
            if (plan.ratePlanId === ratePlanId && plan.roomId === roomId) {
              const updatedRooms = plan.selectedRooms - 1;
              if (updatedRooms > 0) {
                return { ...plan, selectedRooms: updatedRooms };
              }
              return null;
            }
            return plan;
          })
          .filter(Boolean);
      });
    },
    [setBookingSidebar]
  );

  const handleRemoveRoomType = useCallback(
    (roomId) => {
      setBookingSidebar((prev) =>
        prev.filter((plan) => plan.roomId !== roomId)
      );
      setExpandedRooms((prevState) => {
        const updatedState = { ...prevState };
        delete updatedState[roomId];
        return updatedState;
      });
    },
    [setBookingSidebar]
  );

  const handleCloseSidebar = useCallback(() => {
    setShowSidebar(false);
    setBookingSidebar([]);
  }, [setShowSidebar, setBookingSidebar]);

  const toggleRoomExpand = useCallback((roomId) => {
    setExpandedRooms((prevState) => ({
      ...prevState,
      [roomId]: !prevState[roomId],
    }));
  }, []);

  return {
    expandedRooms,
    bookingSidebar,
    handlePickAddOnService,
    handleRemoveAddOnService,
    handlePickRatePlan,
    handleRoomChange,
    handleGuestInput,
    toggleExtraBed,
    handleRemoveIndividualRoom,
    handleRemoveRoomType,
    handleCloseSidebar,
    toggleRoomExpand,
    setBookingSidebar,
  };
};
